// ---------------------------------------------------------------------------
// Orientation guides - the short "where am I" tour every persona starts with.
//
// This one guide is shared by all seven personas, which is the whole design
// constraint: an owner, an accountant and a front-desk host all run it, so it
// may only touch what every one of them can reach. In practice that is the
// dashboard and nothing else - `dashboard.view` is the single permission every
// seeded role has. Anything richer belongs in a persona guide, where the
// journey has already established what this person is allowed to see.
//
// Every anchored step here is marked `optionalTarget`, because the sidebar is a
// drawer on a phone and the getting-started card disappears once it is
// dismissed. Those steps point at the control when it is there and say the same
// words from a centred card when it is not, rather than stalling on a screen
// that is behaving perfectly.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';

const DASHBOARD = PATH_ADMIN.dashboard;

export const ORIENTATION_QUICK = {
  id: 'orientation-quick',
  title: 'Quick tour',
  summary:
    'Three minutes on how DeskOS is put together, what your dashboard is '
    + 'telling you, and where to go when you get stuck.',
  category: 'orientation',
  level: 'quick',
  permission: 'dashboard.view',
  featureSlug: null,
  estMinutes: 3,
  keywords: [
    'tour', 'getting started', 'overview', 'walkthrough', 'orientation',
    'basics', 'how does deskos work', 'new here',
  ],
  readinessKey: null,
  steps: [
    {
      id: 'welcome',
      kind: 'narrate',
      route: DASHBOARD,
      target: CENTER,
      placement: 'center',
      title: 'Welcome to DeskOS',
      body:
        'DeskOS runs the whole operation of a workspace: who sits where, what '
        + 'they pay, who walks in today, and what needs fixing. This is a quick '
        + 'lap of the shape of it. You can pause at any point and pick it up from '
        + 'the same step, and you can replay it as often as you like.',
    },
    {
      id: 'the-spine',
      kind: 'narrate',
      route: DASHBOARD,
      target: CENTER,
      placement: 'center',
      title: 'One spine holds everything together',
      body:
        'Space, plan, member, subscription, invoice, payment. You describe the '
        + 'space and the seats in it. A plan says what a seat costs. A member is '
        + 'a person, and the company behind them is who gets billed. A '
        + 'subscription ties a company to a plan and to actual seats for a '
        + 'period, and that is what makes those seats read as occupied. The '
        + 'invoice comes out of the subscription, and the payment settles the '
        + 'invoice. Almost everything else in DeskOS hangs off that line.',
    },
    {
      id: 'nav',
      kind: 'narrate',
      route: DASHBOARD,
      target: ANCHOR.navDashboard,
      placement: 'right',
      optionalTarget: true,
      title: 'The sidebar is your whole job, grouped',
      body:
        'Front Desk is today: check-ins, visitors, service requests. '
        + 'Memberships is who your customers are. Commerce is the money. Spaces '
        + 'is the building itself. You will not see every group here, because '
        + 'the menu only shows what your role can open. That is normal, not a '
        + 'missing feature.',
    },
    {
      id: 'campus',
      kind: 'narrate',
      route: DASHBOARD,
      target: CENTER,
      placement: 'center',
      title: 'Everything you see belongs to one campus',
      body:
        'A campus is one location. If your organization runs several, the '
        + 'campus you are currently in decides what these screens show, and '
        + 'switching campus switches the data underneath you. Seats, invoices '
        + 'and visitors all belong to a campus. Your organization settings, your '
        + 'team and your branding are shared across all of them.',
    },
    {
      id: 'dashboard',
      kind: 'narrate',
      route: DASHBOARD,
      target: CENTER,
      placement: 'center',
      title: 'This page is the state of the space right now',
      body:
        'Occupancy is seats sold against seats you have. Check-ins are who is '
        + 'physically in today. The rest is whatever needs a human: expiring '
        + 'memberships, unpaid invoices, open requests. If a number here looks '
        + 'wrong, it is almost always a subscription that was never recorded, '
        + 'because this page only ever reports what the system was told.',
    },
    {
      id: 'checklist',
      kind: 'narrate',
      route: DASHBOARD,
      target: ANCHOR.gettingStarted,
      placement: 'bottom',
      optionalTarget: true,
      title: 'Your next steps live here',
      body:
        'This card tracks what is still missing before your space is ready to '
        + 'run, and it is built from your role, so it only ever asks you for '
        + 'things you can actually do. "Walk me through it" starts a real guide '
        + 'on the real screen, with your real data.',
    },
    {
      id: 'help',
      kind: 'narrate',
      route: DASHBOARD,
      target: ANCHOR.helpMenu,
      placement: 'bottom',
      optionalTarget: true,
      title: 'And this is where you come back',
      body:
        'Every guide can be replayed from this help menu, in any order, forever, '
        + 'under "Show me how". Next to it is "Ask AI": describe what you are '
        + 'trying to do in your own words and it will explain, then offer to '
        + 'walk you through it on screen if you would rather be shown than told.',
    },
  ],
};

export default [ORIENTATION_QUICK];
